import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { SaasError } from '../lib/saas-client';

/** The page an invite email links to (`willow-saas invite-account`): Willow
 *  set the account up, and the customer sets its password. saas verifies the
 *  email and signs in, so this goes straight to the account. */
export function SetPasswordPanel() {
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Read once, then taken out of the address bar, so the link's token isn't
  // left in the history, a bookmark, or a screenshot.
  const [token] = useState(() => params.get('token') ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkRefused, setLinkRefused] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('token')) return;
    url.searchParams.delete('token');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }, []);

  if (!token) {
    return (
      <div className="auth-page">
        <section className="panel auth-panel" data-testid="set-password-missing">
          <header className="page-header">
            <h1>Link missing</h1>
            <p>Open this page from the link in your Willow email.</p>
          </header>
          <p className="auth-switch">
            <Link to="/login">Sign in →</Link>
          </p>
        </section>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLinkRefused(false);
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await acceptInvite(token, password);
      navigate('/account', { replace: true });
    } catch (err) {
      // saas answers 400 for the link (used, expired, or replaced by a newer
      // one) and for a password it won't take, whose message says so.
      const refused =
        err instanceof SaasError && err.status === 400 && !/^password/i.test(err.message);
      setLinkRefused(refused);
      setError(
        refused
          ? 'This link has expired or was already used.'
          : err instanceof SaasError
            ? err.message
            : 'Could not set the password.',
      );
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <section className="panel auth-panel" data-testid="set-password">
        <header className="page-header">
          <h1>Set your password</h1>
          <p>Willow has set up an RPC account for you. Choose a password to sign in.</p>
        </header>
        <form onSubmit={onSubmit} className="auth-form">
          <label>
            <span>Password</span>
            <input
              type="password"
              required
              minLength={12}
              maxLength={256}
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="set-password-new"
            />
            <small className="muted">At least 12 characters.</small>
          </label>
          <label>
            <span>Confirm password</span>
            <input
              type="password"
              required
              minLength={12}
              maxLength={256}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="set-password-confirm"
            />
          </label>
          {error && (
            <p className="form-error" data-testid="set-password-error">
              {error}
              {linkRefused && (
                <>
                  {' '}
                  Ask Willow for a new one, or <Link to="/forgot-password">reset your password</Link>.
                </>
              )}
            </p>
          )}
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="set-password-submit">
            {busy ? 'Setting…' : 'Set password and sign in'}
          </button>
        </form>
      </section>
    </div>
  );
}
